import { CalendarDays } from '@tamagui/lucide-icons';
import { Button } from '@unistocks/components/button/button';
import { DatePicker } from '@unistocks/components/date-picker/date-picker/date-picker';
import { DateValue } from '@unistocks/components/date-picker/types';
import { Group } from '@unistocks/components/group/group';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { Sheet } from '@unistocks/components/sheet/sheet';
import {
  ListRenderItem,
  VirtualList,
  VirtualListProps
} from '@unistocks/components/virtual-list/list';
import { DateConstant } from '@unistocks/proxy/axios-client';
import { formatDate, t } from '@unistocks/shared/localization/index.ts';
import { getDateRange } from '@unistocks/shared/utils/date-utils';
import { translateEnumOptions } from '@unistocks/shared/utils/enum-utils';
import { dateConstantOptions } from '@unistocks/shared/utils/enums/date-constant';
import { createDate, isArray, isNumber } from '@unistocks/shared/utils/util';
import { DateRange, Option, SelectOption } from '@unistocks/types';
import { Nullable } from '@unistocks/types/util/nullable';
import React, { useMemo, useState } from 'react';
import { Adapt, ListItem, Popover, Separator, useAdaptIsActive, useMedia } from 'tamagui';

type Value = Nullable<DateConstant | DateRange<string> | string>;

export type DatePeriodSelectProps = {
  filterOptions?: (dateConstant: Option<DateConstant>) => boolean;
  customOptions?: SelectOption<Nullable<DateRange<string>>>[];
  disabled?: boolean;
  onChange: (value: Nullable<DateRange>) => void;
  datepicker?: boolean;
  minDate?: DateValue;
  maxDate?: DateValue;
  selectionStart?: DateValue;
  selectionEnd?: DateValue;
  minimized?: boolean;
};

export const DatePeriodSelect: React.FC<DatePeriodSelectProps> = ({
  filterOptions = allowAll,
  customOptions,
  onChange,
  disabled,
  datepicker,
  minDate,
  maxDate,
  selectionStart,
  selectionEnd,
  minimized
}) => {
  const [open, setOpen] = useState(false);

  const selectedRange = useMemo<DateRange<string> | undefined>(() => {
    if (selectionStart && selectionEnd) {
      return [createDate(selectionStart).toISODate(), createDate(selectionEnd).toISODate()];
    }
  }, [selectionEnd, selectionStart]);

  const dateButtons = useMemo<Option<Value>[]>(() => {
    const dateConstants = translateEnumOptions(dateConstantOptions.filter(filterOptions));

    return [
      ...dateConstants,
      ...(customOptions ?? []),
      ...(datepicker ? [{ key: t('::Dates'), value: 'custom-dates' }] : [])
    ];
  }, [customOptions, datepicker, filterOptions]);

  const extraData = useMemo(() => {
    const lastDateConstant = dateButtons.findLastIndex(({ value }) => isNumber(value));
    return {
      lastDateConstant,
      disabled
    };
  }, [dateButtons, disabled]);

  const onSelect = (value: Nullable<DateRange<string>>) => {
    onChange(value ? (value.map(createDate) as DateRange) : null);
    setOpen(false);
  };

  const renderContent = () => (
    <DatePeriodSelectContent
      mt={minimized ? undefined : '$3'}
      data={dateButtons}
      disabled={disabled}
      onSelect={onSelect}
      minDate={minDate}
      maxDate={maxDate}
      selectedRange={selectedRange}
      extraData={extraData}
    />
  );

  if (minimized) {
    return (
      <Popover
        stayInFrame
        allowFlip
        placement="bottom-end"
        size={'$5'}
        open={open}
        onOpenChange={setOpen}
      >
        <Popover.Trigger asChild>
          <Button
            size={'$2.5'}
            theme={'unistocksAlt'}
            Icon={CalendarDays}
            pos="absolute"
            bottom="$4"
            right="$2"
            zIndex={1}
            o={0.5}
            hoverStyle={{ o: 1 }}
            pressStyle={{ o: 1 }}
            $platform-native={{
              right: '$1'
            }}
          />
        </Popover.Trigger>

        <Popover.Content
          transition={'quickLessBouncy'}
          elevation={'$1'}
          borderRadius={'$4'}
          borderWidth={1}
          p={'$3'}
          borderColor="$borderColor"
          enterStyle={{ y: -10, o: 0 }}
          exitStyle={{ y: -10, o: 0 }}
        >
          <Popover.Arrow borderWidth={1} />
          {renderContent()}
        </Popover.Content>

        <Adapt when="max-md">
          <Sheet
            enableDynamicSizing
            headerTitle={t('::SelectWhat', {
              0: t('::Dashboard:Settings:DateRange').toLocaleLowerCase()
            })}
            headerIcon={<HeaderIcon Icon={CalendarDays} color={'$headingFont'} />}
            scrollViewProps={{ contentContainerStyle: { paddingHorizontal: 0 } }}
          >
            <Adapt.Contents />
          </Sheet>
        </Adapt>
      </Popover>
    );
  }

  return renderContent();
};

DatePeriodSelect.displayName = 'DatePeriodSelect';

type DatePeriodSelectContentProps = Omit<VirtualListProps<Option<Value>>, 'renderItem'> & {
  disabled?: boolean;
  onSelect: (value: Nullable<DateRange<string>>) => void;
  minDate?: DateValue;
  maxDate?: DateValue;
  selectedRange: DateRange<string> | undefined;
  extraData?: any;
};

const DatePeriodSelectContent: React.FC<DatePeriodSelectContentProps> = ({
  data,
  selectedRange,
  disabled,
  minDate,
  maxDate,
  extraData,
  onSelect,
  ...viewProps
}) => {
  const isAdaptActive = useAdaptIsActive();

  return isAdaptActive ? (
    <VerticalDateList
      dateButtons={data!}
      disabled={disabled}
      onSelect={onSelect}
      minDate={minDate}
      maxDate={maxDate}
      selectedRange={selectedRange}
    />
  ) : (
    <HorizontalDateList
      data={data}
      disabled={disabled}
      onSelect={onSelect}
      minDate={minDate}
      maxDate={maxDate}
      selectedRange={selectedRange}
      extraData={extraData}
      {...viewProps}
    />
  );
};

DatePeriodSelectContent.displayName = 'DatePeriodSelectContent';

type DateListBaseProps = {
  disabled?: boolean;
  onSelect: (value: Nullable<DateRange<string>>) => void;
  minDate?: DateValue;
  maxDate?: DateValue;
  selectedRange?: DateRange<string> | undefined;
};

const HorizontalDateList: React.FC<
  DateListBaseProps & Omit<VirtualListProps<Option<Value>>, 'renderItem'>
> = ({ data, disabled, onSelect, minDate, maxDate, selectedRange, extraData, ...props }) => {
  const media = useMedia();

  const renderItem: ListRenderItem<Option<Value>> = ({ item, index }) => {
    const isCustomDates = item.value === 'custom-dates';
    const isFirst = index === 0;
    const isLastDateConstant = index === extraData.lastDateConstant;

    const onPress = () => {
      if (isArray(item.value)) {
        onSelect(item.value);
      } else if (item.value) {
        onSelect(
          getDateRange(item.value as DateConstant).map(date =>
            date.toISODate()
          ) as DateRange<string>
        );
      } else {
        onSelect(null);
      }
    };

    const button = (
      <Button
        theme={'unistocksAlt'}
        size={'$2'}
        ml={isFirst ? undefined : '$1.5'}
        mr={isLastDateConstant ? '$2' : undefined}
        disabled={disabled}
        onPress={isCustomDates ? undefined : onPress}
      >
        <Button.Text fw={'$7'}>{item.key}</Button.Text>
      </Button>
    );

    if (isCustomDates) {
      return (
        <DatePicker
          type="range"
          withCellSpacing={false}
          keepChildrenMounted={false}
          value={selectedRange}
          defaultDate={selectedRange?.at(-1)}
          minDate={minDate}
          maxDate={maxDate}
          placement="top-end"
          numberOfColumns={media.md ? 2 : 1}
          onChange={([start, end]) => {
            onSelect(start && end ? [start, end] : null);
          }}
        >
          {button}
        </DatePicker>
      );
    }

    return button;
  };

  return (
    <VirtualList
      horizontal
      data={data}
      extraData={extraData}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        ml: 'auto'
      }}
      {...props}
    />
  );
};

HorizontalDateList.displayName = 'HorizontalDateList';

const VerticalDateList: React.FC<
  DateListBaseProps & { dateButtons: VirtualListProps<Option<Value>>['data'] }
> = ({ dateButtons, disabled, onSelect, minDate, maxDate, selectedRange }) => {
  return (
    <Group render="ul" separator={<Separator />}>
      {dateButtons?.map(item => {
        const isCustomDates = item.value === 'custom-dates';

        const onPress = () => {
          if (isArray(item.value)) {
            onSelect(item.value);
          } else if (item.value) {
            onSelect(
              getDateRange(item.value as DateConstant).map(date =>
                date.toISODate()
              ) as DateRange<string>
            );
          } else {
            onSelect(null);
          }
        };

        if (isCustomDates) {
          return (
            <DatePicker
              key={keyExtractor(item)}
              type="range"
              withCellSpacing={false}
              keepChildrenMounted={false}
              minDate={minDate}
              maxDate={maxDate}
              placement="top-end"
              value={selectedRange}
              onChange={([start, end]) => onSelect(start && end ? [start, end] : null)}
            >
              <Group.Item>
                <ListItem
                  cursor="pointer"
                  size={'$5'}
                  gap={'$3'}
                  disabled={disabled}
                  title={item.key}
                />
              </Group.Item>
            </DatePicker>
          );
        }

        return (
          <Group.Item key={keyExtractor(item)}>
            <ListItem
              cursor="pointer"
              size={'$5'}
              gap={'$3'}
              borderRadius={'$4'}
              disabled={disabled}
              onPress={onPress}
            >
              <ListItem.Title>{item.key}</ListItem.Title>
            </ListItem>
          </Group.Item>
        );
      })}
    </Group>
  );
};

VerticalDateList.displayName = 'VerticalDateList';

const keyExtractor = ({ value, key }: Option<Value>) =>
  isArray(value) ? value.map(d => formatDate(d)).join('-') : key;
const allowAll = () => true;
